import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { useQueryWithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import {
  infrastructureEventResponseSchema,
  InfrastructureEventType,
  InfrastructureResponse,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { isNotNullish } from "shared/utils/objectUtils";

export const eventByUuidQueryKey = "infrastructureEvent";

type Response = (InfrastructureEventType | null)[];

/**
 * Message context not supported for swedish infrastructure events.
 */
export type UseInfrastructureEventOptions = {
  messageContext?: string | null;
};

/**
 * This naming is a bit awkward, but thats because it is an specific usage only
 * for providing infrastructureEvent for trainBatchModal. In all other
 * usages of use[Infra/Station/Train]Event, internal messages are necessary
 */
export const useInfrastructureEventWithoutInternalMessages = (
  uuids: string[],
  options?: UseInfrastructureEventOptions,
) =>
  useQuery<InfrastructureResponse[]>({
    queryKey: [eventByUuidQueryKey, ...uuids, options?.messageContext],
    queryFn: async ({ signal }) => {
      const { data } = await axiosClient.get<Response>(
        `${getBackendUrl()}/infrastructure-event?uuids=${uuids.join("&uuids=")}`,
        {
          signal,
          params: {
            messageContext: options?.messageContext ?? null,
          },
        },
      );
      return data.map((infrastructureEvent) => {
        const { error } =
          infrastructureEventResponseSchema.safeParse(infrastructureEvent);
        if (error) {
          log(
            LogLevel.error,
            "Infrastructure event response schema error",
            error.message,
          );
        }
        const parsedSchema =
          infrastructureEventResponseSchema.parse(infrastructureEvent);
        return parsedSchema.infrastructureInformation;
      });
    },
    staleTime: 0,
    enabled: Boolean(uuids.length),
  });

export const useInfrastructureEvent = (
  uuids: string[],
  options?: UseInfrastructureEventOptions,
) =>
  useQueryWithInternalMessages(
    useInfrastructureEventWithoutInternalMessages(uuids, options),
  );

export const useVersionedInfrastructureEvent = (uuid: string) =>
  useQuery<InfrastructureEventType[]>({
    queryKey: [eventByUuidQueryKey, uuid],
    queryFn: async ({ signal }) => {
      const { data } = await axiosClient.get<Response>(
        `${getBackendUrl()}/infrastructure-event`,
        {
          signal,
          params: {
            uuids: uuid,
            latest: false,
          },
        },
      );

      return data
        .filter(
          (
            infrastructureEvent,
          ): infrastructureEvent is InfrastructureEventType =>
            isNotNullish(infrastructureEvent),
        )
        .map((infrastructureEvent) => {
          const { error } =
            infrastructureEventResponseSchema.safeParse(infrastructureEvent);
          if (error) {
            log(
              LogLevel.error,
              "Infrastructure event response schema error",
              error.message,
            );
          }

          return infrastructureEvent;
        });
    },
    staleTime: 0,
    enabled: Boolean(uuid),
  });
